import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  SvgIcon,
  TextField,
  Typography
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import React, { FC, useMemo, useState } from 'react';
import { AuditListingProps, CustomKeyboardEvent } from 'src/types';
import { Search as SearchIcon } from 'react-feather';
import { useInventoryAudit } from 'src/hooks/inventory-audit';
import { colors } from 'src/constants';

interface Props {
  open: boolean;
  toggleOpen: () => void;
}

export const MobileAuditSerialsDialog: FC<Props & AuditListingProps> = ({
  id = undefined,
  productName = '',
  open = false,
  scannedSerialNos = [],

  toggleOpen
}) => {
  const {
    getProductListingViaSerialNo,
    deleteSerialsForProduct
  } = useInventoryAudit();

  const [scanSn, setScanSn] = useState<string>('');
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(
    new Set()
  );

  const isAllSerialsSelected = useMemo(
    () => selectedSerials.size === scannedSerialNos.length,
    [scannedSerialNos.length, selectedSerials.size]
  );

  const selectAllButtonLabel = isAllSerialsSelected
    ? 'Deselect All'
    : 'Select All';

  const onHandleScanSn = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScanSn(event?.target?.value);
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      getProductListingViaSerialNo(scanSn);
      setScanSn('');
    }
  };

  const toggleSerial = (serial: string) => {
    setSelectedSerials((prev) => {
      const updated = new Set(prev);
      if (updated.has(serial)) {
        updated.delete(serial);
      } else {
        updated.add(serial);
      }
      return updated;
    });
  };

  const handleSelectAll = () => {
    if (selectedSerials.size === scannedSerialNos.length) {
      setSelectedSerials(new Set()); // deselect all
    } else {
      setSelectedSerials(new Set(scannedSerialNos)); // select all
    }
  };

  const handleSerialDeletion = (id: number = 0, serials: string[]) => {
    if (!id) {
      console.error('handleSerialDeletion error invalid product id');
      return;
    }
    deleteSerialsForProduct(id, serials);
  };

  return (
    <Dialog open={open} onClose={toggleOpen} fullScreen>
      <DialogTitle>
        <Typography
          style={{
            fontSize: '.7em',
            marginBottom: '1em',
            color: colors.grey[600]
          }}
        >
          {productName}
        </Typography>

        <Typography variant="h6">Manage Serials</Typography>

        <TextField
          onChange={onHandleScanSn}
          value={scanSn}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon fontSize="small" color="action">
                  <SearchIcon />
                </SvgIcon>
              </InputAdornment>
            )
          }}
          placeholder="Scan Or Type Product Serial No"
          variant="outlined"
          onKeyPress={onKeyPress}
        />
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            justifyContent: 'end',
            display: 'flex',
            columnGap: '1em',
            marginBottom: '1em'
          }}
        >
          {selectedSerials?.size > 0 ? (
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                handleSerialDeletion(id, Array.from(selectedSerials))
              }
              color="secondary"
            >
              Delete Selected
            </Button>
          ) : null}

          <Button
            size="small"
            variant={isAllSerialsSelected ? 'contained' : 'outlined'}
            onClick={handleSelectAll}
            color="primary"
          >
            {selectAllButtonLabel}
          </Button>
        </div>

        <Card
          elevation={2}
          style={{
            padding: '.5em',
            border: '1px solid #ddd',
            borderRadius: '5'
          }}
        >
          <PerfectScrollbar style={{ height: '60vh' }}>
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '.5em'
              }}
            >
              {scannedSerialNos?.map((s, ind) => (
                <Card
                  onClick={() => toggleSerial(s)}
                  key={ind}
                  style={{
                    padding: '0 1em',
                    border: '1px solid #ddd',
                    borderRadius: '5'
                  }}
                >
                  <Grid container>
                    <Grid
                      item
                      xs={10}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Typography>{s}</Typography>
                    </Grid>
                    <Grid item xs>
                      <Checkbox
                        color="primary"
                        checked={selectedSerials.has(s)}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>
          </PerfectScrollbar>
        </Card>

        <div style={{ justifyContent: 'end', display: 'flex' }}>
          <Button variant="text" onClick={toggleOpen} color="secondary">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
